import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { themeColours } from 'fym-shared/src/music24/releaseThemes';
import {
  SPOTIFY_HEADER_DEFAULT_FOCUS,
  SPOTIFY_HEADER_DEFAULT_SOURCE,
  spotifyHeaderSourceMoves,
  type SpotifyHeaderSource,
} from 'fym-shared/src/music24/spotifyHeader';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { Segmented } from '../../../components/primitives/Segmented';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { useOpenCoverEditor } from '../../../../coverEditor/entry/useOpenCoverEditor';
import { useThemeStillSong } from '../../releases/theme/useThemeStillSong';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { studioCredits } from '../studioRules';
import { studioMessages } from '../studioMessages';
import { StudioThemeDialog } from '../StudioThemeDialog';
import type { DetailsStepProps } from '../details/detailsStepProps';
import { FromYourTheme } from '../details/imageTiles/FromYourTheme';
import { StudioRail } from '../details/StudioRail';
import { RailNotice } from '../details/RailNotice';
import { HeaderSourceTiles } from './HeaderSourceTiles';
import { HeaderPicturePicker } from './HeaderPicturePicker';
import { HeaderPhotoPicker } from './HeaderPhotoPicker';
import { HeaderPositionFields } from './HeaderPositionFields';
import { SpotifyHeaderFrame, type HeaderPreviewGround } from './SpotifyHeaderFrame';
import type { HeaderView } from './spotifyHeaderGeometry';
import { useArtistProfile } from './useArtistProfile';
import { useHeaderPictures, type HeaderPicture } from './useHeaderPictures';
import { spotifyHeaderMessages as messages } from './spotifyHeaderMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import studioStyles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Step 3 of the Spotify header (design spotify-header a1 to a6, spec 2026-10-17): the picture
 * (Blurred cover, Your photo, Picture bank, AI picture, Theme colours), the picker of the picked
 * source, Position for a picture that moves, the read-only "From your theme" line, and the rail
 * with the live preview: Desktop or Phone, the name and the photo that Spotify draws, the crop
 * guides. The browser draws the preview with the table of the render (fym-shared
 * `spotifyHeader.ts`). An image: no Instructions, 0 credits.
 */
const SpotifyHeaderDetailsStepComponent: React.FC<DetailsStepProps> = (props) => {
  const { accountId, item, themeId, details, onChange, items, state, isOwner, isMaking, isLookSaving } = props;
  const { songTheme, onMake, actionFeedback } = props;
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const stillSong = useThemeStillSong(item);
  const profile = useArtistProfile(accountId);
  const { clipWatermark } = useMusic24Capabilities();
  const pictures = useHeaderPictures({ accountId, catalogItemUuid: item.uuid, themeId: songTheme.look.themeId });
  const openCoverEditor = useOpenCoverEditor({ accountId });
  const [view, setView] = useState<Exclude<HeaderView, 'file'>>('desktop');
  const [showName, setShowName] = useState(true);
  const [guides, setGuides] = useState(true);
  const [themeOpen, setThemeOpen] = useState(false);
  const colours = useMemo(
    () => themeColours(songTheme.look.themeId, songTheme.palette),
    [songTheme.look.themeId, songTheme.palette]
  );

  const source = details.headerSource ?? SPOTIFY_HEADER_DEFAULT_SOURCE;
  const focus = useMemo(
    () => ({
      x: details.focusX ?? SPOTIFY_HEADER_DEFAULT_FOCUS.x,
      y: details.focusY ?? SPOTIFY_HEADER_DEFAULT_FOCUS.y,
    }),
    [details.focusX, details.focusY]
  );
  const coverGround: HeaderPreviewGround = useMemo(
    () => ({ kind: 'cover', artworkUrl: stillSong.artworkUrl }),
    [stillSong.artworkUrl]
  );
  const pictureOf = useCallback(
    (url: string | undefined): HeaderPreviewGround =>
      url === undefined ? coverGround : { kind: 'picture', url, focus },
    [coverGround, focus]
  );
  const pickedPictureUrl = useMemo(() => {
    if (source === 'bank') {
      return pictures.bank.find((picture) => picture.uuid === details.headerPictureUuid)?.url;
    }
    if (source === 'ai') {
      return pictures.ai.find((picture) => picture.uuid === details.headerPictureUuid)?.url;
    }
    if (source === 'photo') {
      return details.headerPhotoUrl;
    }
    return undefined;
  }, [details.headerPhotoUrl, details.headerPictureUuid, pictures.ai, pictures.bank, source]);

  const ground: HeaderPreviewGround =
    source === 'theme' ? { kind: 'theme' } : source === 'cover' ? coverGround : pictureOf(pickedPictureUrl);
  const tileGrounds = useMemo((): Readonly<Record<SpotifyHeaderSource, HeaderPreviewGround>> => {
    const still = (url: string | undefined): HeaderPreviewGround =>
      url === undefined ? coverGround : { kind: 'picture', url, focus: SPOTIFY_HEADER_DEFAULT_FOCUS };
    return {
      cover: coverGround,
      photo: still(details.headerPhotoUrl ?? profile.photo?.url),
      bank: still(pictures.bank[0]?.thumbUrl),
      ai: still(pictures.ai[0]?.thumbUrl),
      theme: { kind: 'theme' },
    };
  }, [coverGround, details.headerPhotoUrl, pictures.ai, pictures.bank, profile.photo]);

  const pickSource = useCallback(
    (next: SpotifyHeaderSource) => {
      if (next === source) {
        return;
      }
      const patch: typeof details = { headerSource: next, headerPictureUuid: undefined };
      if (next === 'bank' && pictures.bank[0] !== undefined) {
        patch.headerPictureUuid = pictures.bank[0].uuid;
      }
      if (next === 'ai' && pictures.ai[0] !== undefined) {
        patch.headerPictureUuid = pictures.ai[0].uuid;
      }
      if (next === 'photo' && details.headerFileId === undefined && profile.photo !== null) {
        patch.headerFileId = profile.photo.fileId;
        patch.headerPhotoUrl = profile.photo.url;
      }
      onChange(patch);
    },
    [details, onChange, pictures.ai, pictures.bank, profile.photo, source]
  );
  const pickPicture = useCallback(
    (picture: HeaderPicture) => onChange({ headerPictureUuid: picture.uuid }),
    [onChange]
  );
  const pickPhoto = useCallback(
    (photo: { fileId: number; url: string }) => onChange({ headerFileId: photo.fileId, headerPhotoUrl: photo.url }),
    [onChange]
  );
  const moveFocus = useCallback((patch: { focusX?: number; focusY?: number }) => onChange(patch), [onChange]);
  const credits = useMemo(() => studioCredits(themeId, items), [items, themeId]);

  let blockedReason: string | null = null;
  if (state === 'open' && isLookSaving) {
    blockedReason = intl.formatMessage(studioMessages.blockedSavingTheme);
  } else if (state === 'open' && items === null) {
    blockedReason = intl.formatMessage(studioMessages.blockedFields);
  }

  const songTitle = stillSong.title;
  const aiNone = (
    <div className={styles.small}>
      {intl.formatMessage(messages.aiNone, { title: songTitle })}{' '}
      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => openCoverEditor(item.uuid)}>
        {intl.formatMessage(messages.aiOpenEditor)}
      </button>
    </div>
  );
  const offTiles = useMemo(
    () =>
      !pictures.isLoading && pictures.ai.length === 0
        ? {
            ai: {
              text: intl.formatMessage(messages.aiNone, { title: songTitle }),
              action: { label: intl.formatMessage(messages.aiOpenEditor), onClick: () => openCoverEditor(item.uuid) },
            },
          }
        : undefined,
    [intl, item.uuid, openCoverEditor, pictures.ai.length, pictures.isLoading, songTitle]
  );

  const previewWidth = narrow ? Math.min(320, window.innerWidth - 64) : view === 'phone' ? 260 : 316;
  const preview = (
    <div className={styles.rail}>
      <Segmented
        ariaLabel={intl.formatMessage(messages.viewLabel)}
        value={view}
        onChange={setView}
        options={[
          { value: 'desktop', label: intl.formatMessage(messages.desktop) },
          { value: 'phone', label: intl.formatMessage(messages.phone) },
        ]}
      />
      <div className={styles.stage}>
        <SpotifyHeaderFrame
          width={previewWidth}
          view={view}
          ground={ground}
          colours={colours}
          overlay={showName ? { artist: profile.name ?? stillSong.artist, avatarUrl: profile.avatarUrl } : null}
          guides={guides}
          watermark={clipWatermark}
          testId="spotify-header-preview"
        />
      </div>
      <div className={styles.toggles}>
        <div className="form-check form-switch m-0">
          <input
            id="spotify-header-show-name"
            type="checkbox"
            className="form-check-input"
            checked={showName}
            onChange={(event) => setShowName(event.currentTarget.checked)}
          />
          <label className="form-check-label" htmlFor="spotify-header-show-name">
            {intl.formatMessage(messages.showName)}
          </label>
        </div>
        <div className="form-check form-switch m-0">
          <input
            id="spotify-header-guides"
            type="checkbox"
            className="form-check-input"
            checked={guides}
            onChange={(event) => setGuides(event.currentTarget.checked)}
          />
          <label className="form-check-label" htmlFor="spotify-header-guides">
            {intl.formatMessage(messages.cropGuides)}
          </label>
        </div>
      </div>
      {guides && (
        <div className={styles.legend}>
          <span className={styles.legendRow}>
            <i className={styles.keySafe} />
            {intl.formatMessage(messages.legendSafe)}
          </span>
          <span className={styles.legendRow}>
            <i className={styles.keyCrop} />
            {intl.formatMessage(messages.legendCrop)}
          </span>
          <span className={styles.legendRow}>
            <i className={styles.keyName} />
            {intl.formatMessage(messages.legendName)}
          </span>
        </div>
      )}
      {clipWatermark && <div className={styles.freeNote}>{withBrandWord(intl.formatMessage(messages.freeNote))}</div>}
    </div>
  );

  const themeDialog = songTheme.kit !== undefined && (
    <StudioThemeDialog
      isOpen={themeOpen}
      accountId={accountId}
      item={item}
      kit={songTheme.kit}
      onClose={() => setThemeOpen(false)}
    />
  );

  return (
    <div
      className={clsx(studioStyles.details, studioStyles.detailsImage)}
      data-testid="studio-details"
      data-layout="header"
    >
      <div className={studioStyles.detailsMain}>
        <div className={studioStyles.narrowPreview}>{preview}</div>
        <Panel>
          <HeaderSourceTiles
            value={source}
            colours={colours}
            grounds={tileGrounds}
            off={offTiles}
            onChange={pickSource}
          />
          {source === 'bank' && (
            <HeaderPicturePicker
              testId="header-bank-picker"
              title={intl.formatMessage(messages.bankTitle)}
              hint={intl.formatMessage(messages.bankHint, {
                theme: intl.formatMessage(releaseThemeMessages[songTheme.look.themeId].name),
              })}
              pictures={pictures.bank}
              value={details.headerPictureUuid}
              empty={<div className={styles.small}>{intl.formatMessage(messages.bankEmpty)}</div>}
              onPick={pickPicture}
            />
          )}
          {source === 'ai' && (
            <HeaderPicturePicker
              testId="header-ai-picker"
              title={intl.formatMessage(messages.aiTitle, { title: songTitle })}
              hint={intl.formatMessage(messages.aiHint)}
              pictures={pictures.ai}
              value={details.headerPictureUuid}
              empty={aiNone}
              onPick={pickPicture}
            />
          )}
          {source === 'photo' && (
            <HeaderPhotoPicker
              accountId={accountId}
              profile={profile}
              fileId={details.headerFileId}
              photoUrl={details.headerPhotoUrl}
              onPick={pickPhoto}
            />
          )}
          {spotifyHeaderSourceMoves(source) && (
            <HeaderPositionFields
              focusX={focus.x}
              focusY={focus.y}
              showLeftRight={source === 'photo'}
              onChange={moveFocus}
            />
          )}
          <FromYourTheme
            look={songTheme.look}
            palette={songTheme.palette}
            onChangeTheme={songTheme.kit === undefined ? undefined : () => setThemeOpen(true)}
          />
        </Panel>
        {themeDialog}
      </div>
      <StudioRail
        accountId={accountId}
        themeId={themeId}
        details={details}
        releaseThemeId={songTheme.look.themeId}
        style={null}
        credits={credits}
        canMake={items !== null && state === 'open' && !isMaking && !isLookSaving}
        isMaking={isMaking}
        onMake={onMake}
        preview={preview}
        previewNote={intl.formatMessage(messages.honestNote)}
        extraRows={[[intl.formatMessage(messages.pictureTitle), intl.formatMessage(sourceName(source))]]}
        notice={<RailNotice themeId={themeId} state={state} isOwner={isOwner} />}
        blockedReason={blockedReason}
        actionFeedback={actionFeedback}
        wide={false}
      />
    </div>
  );
};

function sourceName(source: SpotifyHeaderSource) {
  switch (source) {
    case 'cover':
      return messages.sourceCover;
    case 'photo':
      return messages.sourcePhoto;
    case 'bank':
      return messages.sourceBank;
    case 'ai':
      return messages.sourceAi;
    case 'theme':
      return messages.sourceTheme;
  }
}

export const SpotifyHeaderDetailsStep = TypedMemo(SpotifyHeaderDetailsStepComponent);
